import React from 'react';
import { Button } from '../../../components/ui/button';
import {
  Check, X, AlertTriangle, Shield, Lock, Eye,
  Globe, Skull, Clock, Ban, Zap
} from 'lucide-react';

export default function OverviewTab({
  securityStats, permanentBans, honeypotVictims, blockedCountries,
  dashboardData, threats, blockIpInput, setBlockIpInput, blockIp,
  lockoutEmail, setLockoutEmail, checkLockout, lockoutStatus, unlockAccount,
  emergencyReset, setPermanentBanIp, setActiveTab, setIpLookup, lookupIp
}) {
  return (
    <div className="space-y-6" data-testid="security-overview-tab">
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
        <div className="bg-green-500/10 rounded-2xl p-5 border border-green-500/30 text-center">
          <Check className="w-8 h-8 text-green-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-green-400">{securityStats.success}</p>
          <p className="text-xs text-muted-foreground">Logins</p>
        </div>
        <div className="bg-red-500/10 rounded-2xl p-5 border border-red-500/30 text-center">
          <X className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-red-400">{securityStats.failed}</p>
          <p className="text-xs text-muted-foreground">Failed</p>
        </div>
        <div className="bg-yellow-500/10 rounded-2xl p-5 border border-yellow-500/30 text-center">
          <Ban className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-yellow-400">{permanentBans.length}</p>
          <p className="text-xs text-muted-foreground">Perm Bans</p>
        </div>
        <div className="bg-purple-500/10 rounded-2xl p-5 border border-purple-500/30 text-center">
          <Skull className="w-8 h-8 text-purple-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-purple-400">{honeypotVictims.length}</p>
          <p className="text-xs text-muted-foreground">Trapped</p>
        </div>
        <div className="bg-blue-500/10 rounded-2xl p-5 border border-blue-500/30 text-center">
          <Globe className="w-8 h-8 text-blue-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-blue-400">{blockedCountries.length}</p>
          <p className="text-xs text-muted-foreground">Countries</p>
        </div>
        <div className="bg-orange-500/10 rounded-2xl p-5 border border-orange-500/30 text-center">
          <Clock className="w-8 h-8 text-orange-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-orange-400">{dashboardData?.summary?.active_auto_bans || 0}</p>
          <p className="text-xs text-muted-foreground">Auto-Bans</p>
        </div>
      </div>

      {/* Auto-Ban Rules */}
      <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-2xl p-6 border border-orange-500/30">
        <h3 className="font-bold text-base mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-400" /> Auto-Ban Rules (Active)
        </h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { val: 5, label: '15 min block + CAPTCHA', color: 'yellow' },
            { val: 10, label: '1 hour block + Alert', color: 'orange' },
            { val: 25, label: '24 hour block', color: 'red' }
          ].map(rule => (
            <div key={rule.val} className={`bg-${rule.color}-500/10 rounded-xl p-4 border border-${rule.color}-500/30 text-center`}>
              <p className={`text-3xl font-bold text-${rule.color}-400`}>{rule.val}</p>
              <p className="text-sm">Failed Attempts</p>
              <p className="text-xs text-muted-foreground mt-1">{rule.label}</p>
            </div>
          ))}
        </div>
        
        {dashboardData?.auto_bans?.length > 0 && (
          <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
            <h4 className="font-medium text-orange-400">Currently Blocked IPs:</h4>
            {dashboardData.auto_bans.map((ban, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                <div>
                  <span className="font-mono font-bold text-red-400">{ban.ip}</span>
                  <p className="text-xs text-muted-foreground">{ban.attempts} failed attempts</p>
                </div>
                <span className="text-orange-400 font-bold">{ban.remaining_minutes} min</span>
              </div>
            ))}
          </div>
        )}
        
        {dashboardData?.admin_alerts?.length > 0 && (
          <div className="mt-4 p-4 bg-red-500/20 rounded-xl border border-red-500/30">
            <h4 className="font-bold text-red-400 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" /> Admin Alerts ({dashboardData.admin_alerts.length})
            </h4>
            {dashboardData.admin_alerts.slice(0, 5).map((alert, i) => (
              <div key={i} className="text-sm p-2 bg-red-500/10 rounded-lg mb-1">
                <span className="text-red-400 font-mono">{alert.ip}</span> triggered auto-ban ({alert.attempts} attempts)
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-card/60 rounded-2xl p-6 border border-border/30">
          <h3 className="font-bold text-base mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-400" /> Quick Block IP
          </h3>
          <div className="flex gap-2">
            <input type="text" placeholder="Enter IP..." value={blockIpInput}
              onChange={(e) => setBlockIpInput(e.target.value)}
              className="flex-1 px-4 py-2 rounded-xl bg-secondary border border-border/30" />
            <Button onClick={() => { blockIp(blockIpInput); setBlockIpInput(''); }} className="bg-yellow-500 hover:bg-yellow-600 text-black">24h</Button>
            <Button onClick={() => { setPermanentBanIp(blockIpInput); setActiveTab('permanent'); }} className="bg-red-500 hover:bg-red-600">Ban</Button>
          </div>
        </div>
        <div className="bg-card/60 rounded-2xl p-6 border border-border/30">
          <h3 className="font-bold text-base mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-yellow-400" /> Account Lockout
          </h3>
          <div className="flex gap-2">
            <input type="email" placeholder="Enter email..." value={lockoutEmail}
              onChange={(e) => setLockoutEmail(e.target.value)}
              className="flex-1 px-4 py-2 rounded-xl bg-secondary border border-border/30" />
            <Button onClick={checkLockout}>Check</Button>
          </div>
          {lockoutStatus && (
            <div className={`mt-3 p-3 rounded-xl ${lockoutStatus.is_locked ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
              <p className="font-medium">{lockoutStatus.is_locked ? 'LOCKED' : 'Not Locked'}</p>
              {lockoutStatus.is_locked && <Button onClick={unlockAccount} className="mt-2 bg-green-500 text-white" size="sm">Unlock</Button>}
            </div>
          )}
        </div>
      </div>

      {/* Emergency Reset */}
      <div className="bg-red-500/10 rounded-2xl p-6 border border-red-500/30">
        <h3 className="font-bold text-base mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-400" /> Emergency Security Reset
        </h3>
        <p className="text-sm text-muted-foreground mb-4">Clear ALL locks and counters to allow legitimate users to login again.</p>
        <Button onClick={emergencyReset} className="bg-red-500 hover:bg-red-600 text-white">Clear All Locks</Button>
      </div>

      {/* Recent Threats */}
      <div className="bg-card/60 rounded-2xl p-6 border border-border/30">
        <h3 className="font-bold text-base mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-400" /> Suspicious IPs
        </h3>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {threats.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No suspicious activity detected</p>
          ) : threats.map((t, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
              <div>
                <span className="font-mono">{t.ip}</span>
                <span className={`ml-2 px-2 py-0.5 rounded text-xs ${t.score >= 50 ? 'bg-red-500/30 text-red-300' : 'bg-yellow-500/30 text-yellow-300'}`}>
                  Score: {t.score}
                </span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setIpLookup(t.ip); setActiveTab('lookup'); }}>
                  <Eye className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => blockIp(t.ip)} className="text-yellow-400">Block</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
